import { jsPDF } from 'jspdf';
import { TestCase } from '../services/testCaseService';

/**
 * Generate a PDF for a single test case
 */
export const generateTestCasePDF = async (testCase: TestCase, projectName?: string): Promise<Blob> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // 1. Cover Page
    pdf.setFillColor(30, 41, 59); // Dark slate background
    pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
    
    pdf.setTextColor(251, 146, 60); // Orange accent
    pdf.setFontSize(40);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TEST CASE', 20, 90);
    pdf.text('SPECIFICATION', 20, 105);
    
    pdf.setDrawColor(251, 146, 60);
    pdf.setLineWidth(1);
    pdf.line(20, 115, 110, 115);
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.text(testCase.tcCode || 'TC-UNKNOWN', 20, 130);
    
    pdf.setFontSize(14);
    const titleLines = pdf.splitTextToSize(testCase.title, pdfWidth - 40);
    pdf.text(titleLines, 20, 140);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    if (projectName) {
        pdf.text(`Project: ${projectName}`, 20, 160);
    }
    pdf.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 165);
    pdf.text('DocSprintAI Test Management Platform', 20, 170);

    // Header/Footer Helper
    const addHeaderFooter = (pageNum: number) => {
        pdf.setFillColor(251, 146, 60);
        pdf.rect(0, 0, pdfWidth, 10, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(`Test Case: ${testCase.tcCode || 'TC'}`, 10, 6);
        pdf.text(`Page ${pageNum}`, pdfWidth - 20, 6);
        
        pdf.setFillColor(30, 41, 59);
        pdf.rect(0, pdfHeight - 8, pdfWidth, 8, 'F');
        pdf.text('QA Documentation | DocSprintAI', 10, pdfHeight - 3);
    };

    // 2. Test Case Details
    pdf.addPage();
    let currentPage = 2;
    addHeaderFooter(currentPage);
    let yPos = 30;

    // Title Section
    pdf.setTextColor(251, 146, 60);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Test Case Details', margin, yPos);
    yPos += 12;

    // Test Case ID and Title
    pdf.setTextColor(30, 41, 59);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`ID: ${testCase.tcCode || 'N/A'}`, margin, yPos);
    yPos += 8;

    pdf.setFontSize(11);
    const titleDetailLines = pdf.splitTextToSize(`Title: ${testCase.title}`, pdfWidth - (margin * 2));
    pdf.text(titleDetailLines, margin, yPos);
    yPos += (titleDetailLines.length * 6) + 8;

    // Description
    if (testCase.description) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Description:', margin, yPos);
        yPos += 6;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(50, 50, 50);
        const descLines = pdf.splitTextToSize(testCase.description, pdfWidth - (margin * 2));
        descLines.forEach((line: string) => {
            if (yPos > pdfHeight - 25) {
                pdf.addPage();
                currentPage++;
                addHeaderFooter(currentPage);
                yPos = 30;
                pdf.setTextColor(50, 50, 50);
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');
            }
            pdf.text(line, margin, yPos);
            yPos += 5;
        });
        yPos += 8;
    }

    // Metadata
    if (yPos > pdfHeight - 40) {
        pdf.addPage();
        currentPage++;
        addHeaderFooter(currentPage);
        yPos = 30;
    }

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPos, pdfWidth - margin, yPos);
    yPos += 8;

    pdf.setTextColor(30, 41, 59);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    
    const metadataY = yPos;
    pdf.text('Type:', margin, metadataY);
    pdf.text('Status:', margin + 60, metadataY);
    pdf.text('Priority:', margin + 120, metadataY);
    
    pdf.setFont('helvetica', 'normal');
    pdf.text(testCase.type || 'Functional', margin, metadataY + 6);
    pdf.text(testCase.status || 'Active', margin + 60, metadataY + 6);
    
    // Priority with color
    const priority = testCase.priority || 'Medium';
    if (priority === 'High') {
        pdf.setTextColor(220, 38, 38);
    } else if (priority === 'Medium') {
        pdf.setTextColor(251, 146, 60);
    } else {
        pdf.setTextColor(59, 130, 246);
    }
    pdf.text(priority, margin + 120, metadataY + 6);
    
    yPos = metadataY + 16;
    pdf.setTextColor(30, 41, 59);

    // 3. Pre-conditions
    if (yPos > pdfHeight - 40) {
        pdf.addPage();
        currentPage++;
        addHeaderFooter(currentPage);
        yPos = 30;
    }

    pdf.setTextColor(251, 146, 60);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Pre-conditions', margin, yPos);
    yPos += 10;

    if (testCase.preConditions && testCase.preConditions.length > 0) {
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        
        testCase.preConditions.forEach((condition, idx) => {
            if (yPos > pdfHeight - 25) {
                pdf.addPage();
                currentPage++;
                addHeaderFooter(currentPage);
                yPos = 30;
                pdf.setTextColor(30, 41, 59);
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');
            }
            
            const condText = `${idx + 1}. ${condition}`;
            const condLines = pdf.splitTextToSize(condText, pdfWidth - (margin * 2 + 5));
            condLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage();
                    currentPage++;
                    addHeaderFooter(currentPage);
                    yPos = 30;
                    pdf.setTextColor(30, 41, 59);
                    pdf.setFontSize(10);
                }
                pdf.text(line, margin + 5, yPos);
                yPos += 5;
            });
            yPos += 3;
        });
    } else {
        pdf.setTextColor(120, 120, 120);
        pdf.setFont('helvetica', 'italic');
        pdf.text('No pre-conditions specified.', margin + 5, yPos);
        yPos += 6;
    }
    
    yPos += 8;

    // 4. Test Steps
    if (yPos > pdfHeight - 40) {
        pdf.addPage();
        currentPage++;
        addHeaderFooter(currentPage);
        yPos = 30;
    }

    pdf.setTextColor(251, 146, 60);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Test Steps', margin, yPos);
    yPos += 10;

    if (testCase.steps && testCase.steps.length > 0) {
        testCase.steps.forEach((step, idx) => {
            if (yPos > pdfHeight - 50) {
                pdf.addPage();
                currentPage++;
                addHeaderFooter(currentPage);
                yPos = 30;
            }

            // Step number
            pdf.setFillColor(251, 146, 60);
            pdf.setDrawColor(251, 146, 60);
            pdf.roundedRect(margin, yPos - 4, 60, 8, 2, 2, 'FD');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`STEP ${idx + 1}`, margin + 2, yPos + 2);
            yPos += 10;

            // Action
            pdf.setTextColor(30, 41, 59);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Action:', margin + 5, yPos);
            yPos += 5;
            
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(50, 50, 50);
            const actionLines = pdf.splitTextToSize(step.action, pdfWidth - (margin * 2 + 10));
            actionLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage();
                    currentPage++;
                    addHeaderFooter(currentPage);
                    yPos = 30;
                    pdf.setTextColor(50, 50, 50);
                    pdf.setFontSize(9);
                    pdf.setFont('helvetica', 'normal');
                }
                pdf.text(line, margin + 10, yPos);
                yPos += 4.5;
            });
            yPos += 3;

            // Expected Result
            pdf.setTextColor(30, 41, 59);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Expected Result:', margin + 5, yPos);
            yPos += 5;
            
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(50, 50, 50);
            const resultLines = pdf.splitTextToSize(step.expectedResult, pdfWidth - (margin * 2 + 10));
            resultLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage();
                    currentPage++;
                    addHeaderFooter(currentPage);
                    yPos = 30;
                    pdf.setTextColor(50, 50, 50);
                    pdf.setFontSize(9);
                    pdf.setFont('helvetica', 'normal');
                }
                pdf.text(line, margin + 10, yPos);
                yPos += 4.5;
            });
            yPos += 8;
        });
    } else {
        pdf.setTextColor(120, 120, 120);
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(10);
        pdf.text('No test steps specified.', margin + 5, yPos);
        yPos += 6;
    }

    yPos += 8;

    // 5. Post-conditions
    if (yPos > pdfHeight - 40) {
        pdf.addPage();
        currentPage++;
        addHeaderFooter(currentPage);
        yPos = 30;
    }

    pdf.setTextColor(251, 146, 60);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Post-conditions', margin, yPos);
    yPos += 10;

    if (testCase.postConditions && testCase.postConditions.length > 0) {
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        
        testCase.postConditions.forEach((condition, idx) => {
            if (yPos > pdfHeight - 25) {
                pdf.addPage();
                currentPage++;
                addHeaderFooter(currentPage);
                yPos = 30;
                pdf.setTextColor(30, 41, 59);
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');
            }
            
            const condText = `${idx + 1}. ${condition}`;
            const condLines = pdf.splitTextToSize(condText, pdfWidth - (margin * 2 + 5));
            condLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage();
                    currentPage++;
                    addHeaderFooter(currentPage);
                    yPos = 30;
                    pdf.setTextColor(30, 41, 59);
                    pdf.setFontSize(10);
                }
                pdf.text(line, margin + 5, yPos);
                yPos += 5;
            });
            yPos += 3;
        });
    } else {
        pdf.setTextColor(120, 120, 120);
        pdf.setFont('helvetica', 'italic');
        pdf.text('No post-conditions specified.', margin + 5, yPos);
    }

    return pdf.output('blob');
};

/**
 * Generate a PDF for multiple test cases
 */
export const generateTestCasesBulkPDF = async (
    testCases: TestCase[],
    title: string,
    projectName?: string
): Promise<Blob> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // 1. Cover Page
    pdf.setFillColor(30, 41, 59);
    pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
    
    pdf.setTextColor(251, 146, 60);
    pdf.setFontSize(40);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TEST CASES', 20, 90);
    pdf.text('COLLECTION', 20, 105);
    
    pdf.setDrawColor(251, 146, 60);
    pdf.setLineWidth(1);
    pdf.line(20, 115, 110, 115);
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    if (projectName) {
        pdf.text(projectName.toUpperCase(), 20, 130);
    }
    
    pdf.setFontSize(14);
    pdf.text(title, 20, 140);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Total Test Cases: ${testCases.length}`, 20, 155);
    pdf.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 160);
    pdf.text('DocSprintAI Test Management Platform', 20, 165);

    // Header/Footer Helper
    const addHeaderFooter = (pageNum: number) => {
        pdf.setFillColor(251, 146, 60);
        pdf.rect(0, 0, pdfWidth, 10, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(`Test Cases Collection | ${title}`, 10, 6);
        pdf.text(`Page ${pageNum}`, pdfWidth - 20, 6);
        
        pdf.setFillColor(30, 41, 59);
        pdf.rect(0, pdfHeight - 8, pdfWidth, 8, 'F');
        pdf.text('QA Documentation | DocSprintAI', 10, pdfHeight - 3);
    };

    // 2. Table of Contents
    pdf.addPage();
    let currentPage = 2;
    addHeaderFooter(currentPage);
    let yPos = 30;

    pdf.setTextColor(251, 146, 60);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Table of Contents', margin, yPos);
    yPos += 12;

    pdf.setTextColor(30, 41, 59);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    testCases.forEach((tc, idx) => {
        if (yPos > pdfHeight - 25) {
            pdf.addPage();
            currentPage++;
            addHeaderFooter(currentPage);
            yPos = 30;
            pdf.setTextColor(30, 41, 59);
            pdf.setFontSize(10);
        }
        
        const tocLine = `${idx + 1}. ${tc.tcCode || 'TC'} - ${tc.title}`;
        const tocLines = pdf.splitTextToSize(tocLine, pdfWidth - (margin * 2));
        pdf.text(tocLines, margin, yPos);
        yPos += (tocLines.length * 5) + 2;
    });

    // 3. Individual Test Cases
    testCases.forEach((tc, idx) => {
        pdf.addPage();
        currentPage++;
        addHeaderFooter(currentPage);
        yPos = 30;

        // Test Case Header
        pdf.setTextColor(251, 146, 60);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Test Case ${idx + 1}: ${tc.tcCode || 'TC'}`, margin, yPos);
        yPos += 10;

        // Title
        pdf.setTextColor(30, 41, 59);
        pdf.setFontSize(12);
        const titleLines = pdf.splitTextToSize(tc.title, pdfWidth - (margin * 2));
        pdf.text(titleLines, margin, yPos);
        yPos += (titleLines.length * 6) + 8;

        // Description
        if (tc.description) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.text('Description:', margin, yPos);
            yPos += 5;
            
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(50, 50, 50);
            const descLines = pdf.splitTextToSize(tc.description, pdfWidth - (margin * 2));
            descLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage();
                    currentPage++;
                    addHeaderFooter(currentPage);
                    yPos = 30;
                    pdf.setTextColor(50, 50, 50);
                    pdf.setFontSize(10);
                }
                pdf.text(line, margin, yPos);
                yPos += 5;
            });
            yPos += 6;
        }

        // Metadata
        if (yPos > pdfHeight - 30) {
            pdf.addPage();
            currentPage++;
            addHeaderFooter(currentPage);
            yPos = 30;
        }

        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text(`Type: ${tc.type || 'Functional'}`, margin, yPos);
        pdf.text(`Status: ${tc.status || 'Active'}`, margin + 50, yPos);
        pdf.text(`Priority: ${tc.priority || 'Medium'}`, margin + 100, yPos);
        yPos += 10;

        // Steps summary
        if (tc.steps && tc.steps.length > 0) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Steps (${tc.steps.length}):`, margin, yPos);
            yPos += 6;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            tc.steps.forEach((step, stepIdx) => {
                if (yPos > pdfHeight - 30) {
                    pdf.addPage();
                    currentPage++;
                    addHeaderFooter(currentPage);
                    yPos = 30;
                    pdf.setTextColor(30, 41, 59);
                    pdf.setFontSize(9);
                }

                pdf.setFont('helvetica', 'bold');
                pdf.text(`${stepIdx + 1}. ${step.action}`, margin + 3, yPos);
                yPos += 5;
                
                pdf.setFont('helvetica', 'italic');
                pdf.setTextColor(80, 80, 80);
                const resultLines = pdf.splitTextToSize(`→ ${step.expectedResult}`, pdfWidth - (margin * 2 + 6));
                resultLines.forEach((line: string) => {
                    if (yPos > pdfHeight - 25) {
                        pdf.addPage();
                        currentPage++;
                        addHeaderFooter(currentPage);
                        yPos = 30;
                        pdf.setTextColor(80, 80, 80);
                        pdf.setFontSize(9);
                        pdf.setFont('helvetica', 'italic');
                    }
                    pdf.text(line, margin + 6, yPos);
                    yPos += 4.5;
                });
                yPos += 3;
                pdf.setTextColor(30, 41, 59);
            });
        }
    });

    return pdf.output('blob');
};
